import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  Polyline,
  InfoWindow,
} from "@react-google-maps/api";
import dayjs from "dayjs";
import Loading from "./Loading";
import Firebase from "../lib/Firebase";

const ServiceMap = ({trip, booking}) => {
  const mapRef = useRef(null);
  const shuttleImgRef = useRef(null);
  const stopMarkersRef = useRef([]);
  const shuttleMarkerRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [path, setPath] = useState([]);
  const [routePath, setRoutePath] = useState(null);
  const [stops, setStops] = useState([]);
  const [center, setCenter] = useState({lat: 37.44, lng: -106.59});
  const timestamp = path[path.length - 1]?.timestamp;
  const geocodeTimestamp = dayjs(new Date()).diff(timestamp, "m");
  const [shuttleData] = Firebase.getShuttleGeolocationData(trip?.shuttle.id);

  useEffect(() => {
    if (booking?.pickup_location || booking?.dropoff_location) {
      setStops([booking.pickup_location, booking.dropoff_location]);
    }
  }, [booking.pickup_location, booking.dropoff_location]);

  useEffect(() => {
    if (shuttleData) {
        const { latitude: lat, longitude: lng, timestamp: tmp } = shuttleData;
        setPath((p) => [...p, {lat, lng, timestamp: tmp ? tmp.toDate() : new Date()}]);
    }
  }, [shuttleData]);

  useEffect(() => {
    if (stops.length <= 0) {
      return;
    }

    (async () => {
      const response = await fetch(
        'https://routes.googleapis.com/directions/v2:computeRoutes',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_API_KEY,
            'X-Goog-FieldMask': 'routes.polyline.encodedPolyline',
          },
          body: JSON.stringify({
            origin: { location: { latLng: { latitude: stops[0].latitude, longitude: stops[0].longitude } } },
            destination: { location: { latLng: { latitude: stops[1].latitude, longitude: stops[1].longitude } } },
            travelMode: 'DRIVE',
          }),
        }
      );
      const data = await response.json();
      if (data.routes?.length) {
        const encoded = data.routes[0].polyline.encodedPolyline;
        const decoded = window.google.maps.geometry.encoding.decodePath(encoded);
        setRoutePath(decoded);
      }
    })();

    setCenter({
      lat: stops[0]?.latitude,
      lng: stops[0]?.longitude,
    });
  }, [stops]);

  // Create/update stop markers imperatively using AdvancedMarkerElement
  useEffect(() => {
    if (!mapInstance || stops.length === 0) return;

    (async () => {
      const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker');

      stopMarkersRef.current.forEach(m => { m.map = null; });
      stopMarkersRef.current = [];

      stops.forEach((stop, idx) => {
        const img = document.createElement('img');
        img.src = process.env.PUBLIC_URL + (idx === 0 ? 'images/pickup_pin.png' : 'images/dropoff_pin.png');
        img.style.width = '48px';
        img.style.height = idx === 0 ? '48px' : '60px';
        img.alt = idx === 0 ? 'pickup' : 'dropoff';

        stopMarkersRef.current.push(
          new AdvancedMarkerElement({
            map: mapInstance,
            position: { lat: stop.latitude, lng: stop.longitude },
            content: img,
          })
        );
      });
    })();

    return () => {
      stopMarkersRef.current.forEach(m => { m.map = null; });
      stopMarkersRef.current = [];
    };
  }, [mapInstance, stops]);

  // Create shuttle marker once, then update its position on each path change
  useEffect(() => {
    if (!mapInstance || path.length === 0) return;

    const lastPos = path[path.length - 1];

    if (shuttleMarkerRef.current) {
      shuttleMarkerRef.current.position = lastPos;
    } else {
      (async () => {
        const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker');

        const img = document.createElement('img');
        img.src = process.env.PUBLIC_URL + 'images/icon_van2x.png';
        img.style.width = '40px';
        img.style.height = '40px';
        img.alt = 'shuttle';
        shuttleImgRef.current = img;

        shuttleMarkerRef.current = new AdvancedMarkerElement({
          map: mapInstance,
          position: lastPos,
          content: img,
          zIndex: 999,
        });
      })();
    }
  }, [mapInstance, path]);

  // Rotate shuttle icon based on heading
  useEffect(() => {
    if (path?.length > 1) {
      const point1 = path[path.length - 1];
      const point2 = path[path.length - 2];

      const point1LatLng = new window.google.maps.LatLng(point1.lat, point1.lng);
      const point2LatLng = new window.google.maps.LatLng(point2.lat, point2.lng);

      const angle = window.google.maps.geometry.spherical.computeHeading(
        point1LatLng,
        point2LatLng
      );
      const actualAngle = angle + 90;

      if (shuttleImgRef.current) {
        shuttleImgRef.current.style.transform = `rotate(${actualAngle}deg)`;
        shuttleImgRef.current.style.transition = "transform 0.5s";
      }
    }
  }, [path]);

  useEffect(() => {
    const bounds = new window.google.maps.LatLngBounds();

    if (path.length > 0) {
      const {lat, lng} = path[path.length - 1];
      const shuttleLocation = new window.google.maps.LatLng(lat, lng);
      bounds.extend(shuttleLocation);
    }

    if (stops.length > 0) {
      stops.map((stop) => {
        const location = new window.google.maps.LatLng(stop.latitude, stop.longitude);
        return bounds.extend(location);
      });
    }

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.fitBounds(bounds);
      }
    }, 500);
  }, [path, stops]);

  // Cleanup all markers on unmount
  useEffect(() => {
    return () => {
      stopMarkersRef.current.forEach(m => { m.map = null; });
      if (shuttleMarkerRef.current) shuttleMarkerRef.current.map = null;
    };
  }, []);

  if (routePath === null) {
    return <Loading />;
  }

  return (
    <>
      <GoogleMap
        onLoad={(map) => { mapRef.current = map; setMapInstance(map); }}
        zoom={stops.length <= 0 ? 5 : 12}
        center={center}
        mapContainerClassName="map-container"
        options={{
          zoomControl: true,
          mapTypeControl: false,
          fullscreenControl: false,
          scaleControl: false,
          streetViewControl: false,
          controlSize: 27,
          mapId: process.env.REACT_APP_GOOGLE_MAP_ID,
        }}
      >
        <Polyline
          path={routePath}
          options={{
            strokeColor: 'black',
            strokeOpacity: 1,
            strokeWeight: 3,
            clickable: false,
            draggable: false,
            editable: false,
          }}
        />
        { path.length > 0 &&
            <InfoWindow
              position={path[path.length - 1]}
              options={{ pixelOffset: new window.google.maps.Size(0, -50) }}
            >
              <span style={{color: "#4A525F"}}>
                <div>{trip?.shuttle?.license_plate}</div>
                <div>
                  { geocodeTimestamp > 1 ?
                    `${geocodeTimestamp} minute${geocodeTimestamp > 1? "s" : ""} ago`
                    : "Real-time location"
                  }
                </div>
              </span>
            </InfoWindow>
        }
      </GoogleMap>
    </>
  );
}

export default ServiceMap;
